<template>
  <section id="tentang-kami" class="gr-section gr-about">
    <div class="gr-container">
      <header class="gr-head">
        <p class="gr-eyebrow" aria-hidden="true">
          <span class="gr-eyebrow__index">01</span>
          <span class="gr-eyebrow__rule" />
        </p>
        <h2 class="gr-h2">Tentang Kami</h2>
      </header>

      <div class="gr-about__grid">
        <div class="gr-about__narrative">
          <p class="gr-about__p">
            Gotong Royong Indonesia (GR.ID) adalah sebuah gerakan lingkar
            kerelawanan yang bermula di Yogyakarta sejak tahun 2023.
            Beranggotakan pemuda-pemudi Nusantara yang berasal dari berbagai
            daerah, mulai dari Aceh, Padang, Jambi, Palembang, Jakarta, Jawa
            Barat, Jawa Tengah, Madura, Sulawesi Selatan, hingga NTB, gerakan
            ini terus bertumbuh untuk bersama-sama memberi dan berdampak nyata
            bagi masyarakat.
          </p>
          <p class="gr-about__p">
            Di era Society 5.0, Gotong Royong Indonesia hadir sebagai platform
            inklusif dengan mengusung konsep Ekonomi Kontribusi (Sweat Equity).
            Kami percaya bahwa aksi sosial tidak harus selalu terbentur pada
            keterbatasan finansial. Oleh karena itu, kontribusi utama di dalam
            platform ini dinilai melalui “mata uang abadi”: Waktu, Tenaga, dan
            Pikiran. Setiap kontribusi dan rekam jejak aksi sosial relawan juga
            divalidasi melalui sistem digital (Merit Point System berupa Badge
            dan Tier) yang transparan.
          </p>
          <p class="gr-about__p">
            Selain itu, dalam setiap aksi sosial yang membutuhkan dukungan dana,
            kami menggunakan skema Gotong Royong Collaboration (GRC) yang
            mengedepankan transparansi radikal (radical transparency).
            Penyumbang dana disebut sebagai “Collaborator”, di mana setiap
            rupiah yang masuk teridentifikasi sumbernya secara jelas untuk
            menjaga akuntabilitas dan membangun kepercayaan komunitas. Sebagai
            bentuk komitmen dalam memberdayakan pemuda, kami turut menghadirkan
            Gotong Royong Elaborasi (GRE), di mana anggota GRI bisa mendapatkan
            Pelatihan Keterampilan, Upgrade Skills, dan pengembangan diri
            lainnya.
          </p>

          <h3 class="gr-about__pillars-title">Pilar gerakan</h3>

          <!-- Two independent disclosures, both closed on first render. Vuetify
               supplies aria-expanded and the button semantics; aria-controls
               points at the stable candidate-prefixed panel ids. -->
          <v-expansion-panels
            multiple
            variant="accordion"
            elevation="0"
            class="gr-about__panels"
          >
            <v-expansion-panel class="gr-about__panel">
              <v-expansion-panel-title
                aria-controls="gr-about-gre-panel"
                class="gr-about__panel-title"
              >
                Gotong Royong Elaborasi (GRE)
              </v-expansion-panel-title>
              <v-expansion-panel-text id="gr-about-gre-panel">
                Pelatihan Keterampilan, Upgrade Skills, dan pengembangan diri
                lainnya bagi anggota GRI — ruang untuk tumbuh bersama gerakan,
                bukan hanya berkontribusi di dalamnya.
              </v-expansion-panel-text>
            </v-expansion-panel>
            <v-expansion-panel class="gr-about__panel">
              <v-expansion-panel-title
                aria-controls="gr-about-grc-panel"
                class="gr-about__panel-title"
              >
                Gotong Royong Collaboration (GRC)
              </v-expansion-panel-title>
              <v-expansion-panel-text id="gr-about-grc-panel">
                Skema kolaborasi pendanaan yang mengedepankan transparansi
                radikal. Penyumbang dana disebut Collaborator, dan setiap rupiah
                yang masuk teridentifikasi sumbernya secara jelas untuk menjaga
                akuntabilitas komunitas.
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="gr-about__figure">
          <img
            v-if="!bundledFailed"
            :ref="setImageElement"
            :src="photoSource.src"
            :alt="photoSource.alt"
            class="gr-about__photo"
            width="500"
            height="350"
            @error="handlePhotoError"
          />
          <!-- Neutral surface for a photo that fails before hydration. The
               ratio is held so the layout never jumps. -->
          <div
            v-else
            class="gr-about__photo gr-about__photo--fallback"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
// A fixed asset, not the newest published documentation. This used to read
// `/public-documentations?sort=documentation_recent` and take the first photo,
// so the picture beside "Tentang Kami" changed whenever anything was published
// or archived -- an editorial image moving on its own, decided from a screen
// that never mentioned the homepage.
const PHOTO = {
  src: "/assets/images/dokumentasi-gotong-royong.webp",
  alt: "Dokumentasi kegiatan Gotong Royong Indonesia",
};

const photoSource = PHOTO;

// Kept: if the bundled file itself ever fails, the neutral surface below holds
// the ratio rather than showing a broken-image glyph.
const bundledFailed = ref(false);

const handlePhotoError = (): void => {
  bundledFailed.value = true;
};

const imageElement = ref<HTMLImageElement | null>(null);
const setImageElement = (
  element: Element | ComponentPublicInstance | null,
): void => {
  imageElement.value = element instanceof HTMLImageElement ? element : null;
};

// An image that already failed before hydration never emits `error`.
onMounted(() => {
  const image = imageElement.value;
  if (image?.complete && image.naturalWidth === 0) handlePhotoError();
});
</script>

<style scoped>
.gr-about__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(2rem, 1rem + 4vw, 3.5rem);
}

@media (min-width: 960px) {
  .gr-about__grid {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    align-items: start;
  }
}

.gr-about__narrative {
  min-width: 0;
}

.gr-about__p {
  max-width: 65ch;
  margin: 0 0 1.25rem;
  color: var(--gr-ink-2);
  line-height: 1.75;
}

.gr-about__p:last-of-type {
  margin-bottom: 0;
}

.gr-about__pillars-title {
  margin: 2.5rem 0 0.75rem;
  color: var(--gr-ink);
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1;
  text-transform: uppercase;
}

.gr-about__panels {
  border-top: 1px solid var(--gr-line);
}

.gr-about__panel {
  border-bottom: 1px solid var(--gr-line);
  background-color: transparent;
}

.gr-about__panel :deep(.v-expansion-panel__shadow) {
  display: none;
}

.gr-about__panel :deep(.v-expansion-panel-title) {
  min-height: 56px;
  padding-inline: 0;
  color: var(--gr-ink);
  font-size: 1rem;
  font-weight: 700;
}

.gr-about__panel :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 0 20px;
  color: var(--gr-ink-2);
  line-height: 1.7;
}

.gr-about__figure {
  min-width: 0;
}

/* Fixed height rather than a ratio: `aspect-ratio` plus a height cap makes the
   ratio hold the WIDTH down, which is what shrank the activity detail banner. */
.gr-about__photo {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 500px;
  border: 1px solid var(--gr-line);
  border-radius: 4px;
  background-color: var(--gr-green-tint);
  object-fit: cover;
}

@media (min-width: 960px) {
  .gr-about__figure {
    position: sticky;
    top: 104px;
  }
}
</style>
